import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getMyActivity, type MyActivityEvent } from "@/lib/activityStore";

const VERB_LABELS: Record<string, string> = {
  created: "created",
  updated: "updated",
  commented: "commented on",
  comment_edited: "edited a comment on",
  comment_deleted: "deleted a comment on",
  resolved: "resolved a comment on",
  reopened: "reopened a comment on",
  invited: "invited",
  role_changed: "changed role for",
  removed: "removed",
  left: "left the project",
};

function describe(e: MyActivityEvent): string {
  if (e.verb === "left") return "You left the project";
  const verb = VERB_LABELS[e.verb] || e.verb;
  const label = e.entity_label ? `"${e.entity_label}"` : e.entity_type;
  return `You ${verb} ${label}`;
}

export default function MyActivityPage() {
  const [events, setEvents] = useState<MyActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const load = async (before?: string) => {
    try {
      const data = await getMyActivity(before, 50);
      setEvents((prev) => (before ? [...prev, ...data] : data));
      setHasMore(data.length === 50);
    } catch {
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleLoadMore = () => {
    if (events.length === 0) return;
    setLoadingMore(true);
    load(events[events.length - 1].created_at);
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-foreground flex items-center gap-2">
          <Activity className="h-5 w-5" /> My Activity
        </h1>
        <p className="text-sm text-muted-foreground font-body mt-1">
          Everything you've done, across every project you own or collaborate on
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : events.length === 0 ? (
        <div className="text-center py-16">
          <Activity className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No activity yet</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {events.map((e, i) => (
            <div
              key={`${e.created_at}-${i}`}
              className="flex items-start gap-2 px-3 py-2.5 rounded-md border border-border bg-card text-xs"
            >
              <div className="flex-1 min-w-0">
                <p className="text-foreground">{describe(e)}</p>
                {e.summary && e.verb === "commented" && (
                  <p className="text-muted-foreground mt-0.5 line-clamp-2">"{e.summary}"</p>
                )}
                <Link
                  to={`/studio/project/${e.project_id}`}
                  className="text-[11px] text-accent hover:underline mt-1 inline-block"
                >
                  {e.project_name || "Untitled project"}
                </Link>
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0 font-mono">
                {new Date(e.created_at).toLocaleString()}
              </span>
            </div>
          ))}
          {hasMore && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="w-full mt-2 gap-1.5"
            >
              {loadingMore && <Loader2 className="h-3 w-3 animate-spin" />} Load more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
